import sanitizeHtml from "sanitize-html"

/**
 * Whitelist tag/attribute cho HTML soạn từ editor dùng chung ở CMS (R2g-admin `src/components/RichTextEditor`,
 * dùng cho blog, visa, faq, seo-route, esim, hotels). Hợp đồng chính là output TipTap
 * (docs/plan-refactor/06-Rich-Text-Content-Output.md mục 3) — vẫn giữ tương thích nội dung cũ soạn bằng
 * Jodit giai đoạn 13/08 → 29/09/2026 (mục 7: <img> trần căn bằng float/margin, <p> thụt lề margin-left,
 * <sup>/<sub>, bảng có colgroup/width). Soạn ở CMS, chỉ hiển thị ở đây.
 */
const ALLOWED_TAGS = [
  "p", "h1", "h2", "h3", "h4", "h5", "h6", "blockquote", "hr", "br",
  "strong", "em", "u", "s", "sub", "sup", "span", "mark", "code", "pre",
  "ul", "ol", "li",
  "table", "thead", "tbody", "tr", "td", "th", "colgroup", "col",
  "img", "a",
  // TipTap bọc ảnh trong <div class="image-wrapper"> — class khác của div bị lọc qua ALLOWED_CLASSES.
  "div",
]

const CSS_LENGTH = String.raw`-?\d+(\.\d+)?(px|em|rem|%)?`
const CSS_LENGTH_OR_AUTO = `(${CSS_LENGTH}|auto)`

const ALLOWED_STYLES = {
  "*": {
    color: [/^#[0-9a-fA-F]{3,8}$/, /^rgba?\([\d\s.,%]+\)$/, /^[a-zA-Z]+$/],
    "background-color": [/^#[0-9a-fA-F]{3,8}$/, /^rgba?\([\d\s.,%]+\)$/, /^[a-zA-Z]+$/],
    "font-family": [/^[a-zA-Z0-9\s,'"-]+$/],
    "font-size": [/^\d+(\.\d+)?(px|em|rem|%)$/],
    "font-weight": [/^(normal|bold|\d{3})$/],
    "text-align": [/^(left|center|right|justify)$/],
    // width: [/^\d+(\.\d+)?(px|%)$/],
    // height: [/^\d+(\.\d+)?(px|%)$/],
    width: [/^(\d+(\.\d+)?(px|%)|auto)$/],
    height: [/^(\d+(\.\d+)?(px|%)|auto)$/],
    // Bảng TipTap/Jodit ghi min-width trên <table>/<col>.
    "min-width": [/^\d+(\.\d+)?(px|%)$/],
    // Tương thích Jodit: căn ảnh bằng float / display:block + margin:0 auto, thụt lề đoạn văn bằng margin-left.
    float: [/^(left|right|none)$/],
    display: [/^(block|inline|inline-block)$/],
    margin: [new RegExp(`^${CSS_LENGTH_OR_AUTO}(\\s+${CSS_LENGTH_OR_AUTO}){0,3}$`)],
    "margin-left": [new RegExp(`^${CSS_LENGTH_OR_AUTO}$`)],
    "margin-right": [new RegExp(`^${CSS_LENGTH_OR_AUTO}$`)],
  },
}

/** Class được giữ lại — mọi class khác (vd. `language-*` của code block) bị bỏ. */
const ALLOWED_CLASSES = {
  div: ["image-wrapper"],
}

/** Bọc mỗi <table> trong wrapper scroll ngang — HTML gốc từ editor không tự responsive. */
function wrapTables(html: string): string {
  return html
    .replace(/<table/g, '<div class="prose__table-wrap"><table')
    .replace(/<\/table>/g, "</table></div>")
}

// /**
//  * Chèn caption ngay dưới mỗi <img title="..."> — CMS chỉ cần nhập title 1 lần lúc soạn (thay vì phải
//  * gõ lại thành 1 đoạn văn riêng cho từng ảnh). `.cms-image-caption` cần `display: block` ở CSS phía
//  * trang hiển thị để caption tự xuống dòng dưới ảnh dù ảnh đang nằm inline trong đoạn văn.
//  */
// function injectImageCaptions(html: string): string {
//   return html.replace(/<img([^>]*)>/g, (match, attrs) => {
//     const title = /title="([^"]*)"/.exec(attrs)?.[1]?.trim()
//     return title ? `${match}<span class="cms-image-caption">${title}</span>` : match
//   })
// }

/**
 * Chèn caption `.cms-image-caption` ngay dưới <img title="..."> TRẦN (nội dung cũ soạn bằng Jodit).
 * Ảnh TipTap nằm trong `.image-wrapper` thì bỏ qua — caption của nó vẽ bằng CSS `attr(data-title)`
 * (xem `.rich-text` trong `_rich-text.scss`), chèn thêm span sẽ bị hiện 2 caption.
 * `.cms-image-caption` cần `display: block` ở CSS phía trang hiển thị để tự xuống dòng dưới ảnh.
 */
function injectImageCaptions(html: string): string {
  return html.replace(/(<div\b[^>]*\bclass="image-wrapper"[^>]*>\s*)?<img([^>]*)>/g, (match, wrapperOpen, attrs) => {
    if (wrapperOpen) return match
    const title = /title="([^"]*)"/.exec(attrs)?.[1]?.trim()
    return title ? `${match}<span class="cms-image-caption">${title}</span>` : match
  })
}

/**
 * Sanitize HTML content soạn từ editor dùng chung ở CMS trước khi render bằng dangerouslySetInnerHTML.
 * `wrapTables` (mặc định true) bọc mỗi <table> trong `.prose__table-wrap` để tự scroll ngang —
 * tắt đi nếu caller đã tự xử lý scroll bảng theo cách khác (vd. visa-detail bọc bằng JS riêng
 * để giữ nguyên chiều rộng cột gốc từ CMS).
 */
export function sanitizeCmsHtml(html?: string | null, options?: { wrapTables?: boolean }): string {
  if (!html) return ""

  const clean = sanitizeHtml(html, {
    allowedTags: ALLOWED_TAGS,
    allowedAttributes: {
      "*": ["style"],
      img: ["src", "alt", "width", "height", "style", "title"],
      a: ["href", "target", "rel"],
      table: ["width", "style"],
      td: ["width", "style", "colspan", "rowspan"],
      th: ["width", "style", "colspan", "rowspan"],
      col: ["width", "span", "style"],
      div: ["data-align", "data-title", "style"],
      mark: ["data-color", "style"],
    },
    allowedClasses: ALLOWED_CLASSES,
    allowedStyles: ALLOWED_STYLES,
    selfClosing: [...sanitizeHtml.defaults.selfClosing, "col"],
    allowedSchemes: ["http", "https", "mailto", "tel"],
    transformTags: {
      a: sanitizeHtml.simpleTransform("a", { rel: "noopener noreferrer" }, true),
    },
  })

  const withCaptions = injectImageCaptions(clean)
  return options?.wrapTables === false ? withCaptions : wrapTables(withCaptions)
}
